<template>
  <div
    class="absolute left-0 h-full cursor-move border-b border-gray-700 bg-blue-500/10"
    :class="{
      'cursor-grabbing': dragging,
      'cursor-grab': !dragging && !resizing,
    }"
    :style="{
      width: `${blockWidth}px`,
      left: `${blockLeft}px`,
    }"
    @mousedown="handleDragStart">
    <!-- Video/Image preview thumbnails -->
    <div class="pointer-events-none absolute inset-0 flex items-center overflow-hidden">
      <div
        v-for="(frame, index) in previewFrames"
        :key="index"
        class="h-full border-r border-gray-600 bg-gray-800"
        :style="{ width: `${frameWidth}px` }">
        <img v-if="sourceUrl" :src="getThumbnail(frame)" class="h-full w-full object-cover opacity-50" alt="Preview" />
      </div>
    </div>

    <!-- Resize handles -->
    <div
      class="bg-primary-500/50 hover:bg-primary-500 absolute top-0 bottom-0 left-0 z-10 w-2 cursor-ew-resize"
      @mousedown.stop="startResize('left', $event)"></div>
    <div
      class="bg-primary-500/50 hover:bg-primary-500 absolute top-0 right-0 bottom-0 z-10 w-2 cursor-ew-resize"
      @mousedown.stop="startResize('right', $event)"></div>

    <!-- Duration and Media Start labels -->
    <div class="absolute top-1 left-1 rounded bg-black/50 px-1.5 py-0.5 text-[10px] text-white">
      {{ formatDuration(duration) }}
    </div>
    <div
      v-if="mediaStart > 0"
      class="absolute bottom-1 left-1 rounded bg-black/70 px-1.5 py-0.5 text-[10px] text-blue-300">
      Start: {{ formatDuration(mediaStart) }}
    </div>
  </div>
</template>

<script setup>
import { computed, onUnmounted, ref } from 'vue';

const props = defineProps({
  sourceUrl: {
    type: String,
    required: false,
  },
  startTime: {
    type: Number,
    default: 0,
  },
  duration: {
    type: Number,
    default: 10,
  },
  mediaStart: {
    type: Number,
    default: 0,
  },
  pixelsPerSecond: {
    type: Number,
    required: true,
  },
  objectId: {
    type: String,
    required: true,
  },
  assetType: {
    type: String, // 'IM' for image, 'VI' for video
    required: false,
  },
});

const emit = defineEmits(['update-duration', 'update-start-time', 'update-media-start']);

const resizing = ref(false);
const dragging = ref(false);
const resizeHandle = ref(null); // 'left' or 'right'
const resizeStartX = ref(0);
const dragStartX = ref(0);
const resizeStartDuration = ref(0);
const resizeStartTime = ref(0);
const dragStartTime = ref(0);

const blockWidth = computed(() => props.duration * props.pixelsPerSecond);

const blockLeft = computed(() => props.startTime * props.pixelsPerSecond);

// Calculate number of preview frames to show based on width
const frameWidth = 60; // pixels per frame thumbnail
const previewFrames = computed(() => {
  const numFrames = Math.max(1, Math.floor(blockWidth.value / frameWidth));
  return Array.from({ length: numFrames }, (_, i) => i);
});

const getThumbnail = (frameIndex) => {
  // For now, return the source URL
  // In a real implementation, you'd generate thumbnails at different time positions
  return props.sourceUrl;
};

const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = (seconds % 60).toFixed(1);
  return mins > 0 ? `${mins}:${secs.padStart(4, '0')}` : `${secs}s`;
};

const startResize = (handle, event) => {
  event.preventDefault();
  event.stopPropagation();

  resizing.value = true;
  resizeHandle.value = handle;
  resizeStartX.value = event.clientX;
  resizeStartDuration.value = props.duration;
  resizeStartTime.value = props.startTime;

  document.addEventListener('mousemove', onResize);
  document.addEventListener('mouseup', stopResize);
};

const onResize = (event) => {
  if (!resizing.value) return;

  const deltaX = event.clientX - resizeStartX.value;
  const deltaTime = deltaX / props.pixelsPerSecond;

  if (resizeHandle.value === 'right') {
    // Resize from the right - change duration
    const newDuration = Math.max(1, resizeStartDuration.value + deltaTime);
    emit('update-duration', {
      objectId: props.objectId,
      duration: newDuration,
      isDragging: true,
    });
  } else if (resizeHandle.value === 'left') {
    // Resize from the left - change start time and duration
    const newStartTime = Math.max(0, resizeStartTime.value + deltaTime);
    const deltaStart = newStartTime - resizeStartTime.value;
    const newDuration = Math.max(1, resizeStartDuration.value - deltaStart);

    emit('update-start-time', {
      objectId: props.objectId,
      startTime: newStartTime,
      duration: newDuration,
      isDragging: true,
    });
  }
};

const stopResize = (event) => {
  if (!resizing.value) return;

  const deltaX = event.clientX - resizeStartX.value;
  const deltaTime = deltaX / props.pixelsPerSecond;

  if (resizeHandle.value === 'right') {
    const newDuration = Math.max(1, resizeStartDuration.value + deltaTime);
    emit('update-duration', {
      objectId: props.objectId,
      duration: newDuration,
      isDragging: false,
    });
  } else if (resizeHandle.value === 'left') {
    const newStartTime = Math.max(0, resizeStartTime.value + deltaTime);
    const deltaStart = newStartTime - resizeStartTime.value;
    const newDuration = Math.max(1, resizeStartDuration.value - deltaStart);

    emit('update-start-time', {
      objectId: props.objectId,
      startTime: newStartTime,
      duration: newDuration,
      isDragging: false,
    });
  }

  resizing.value = false;
  resizeHandle.value = null;

  document.removeEventListener('mousemove', onResize);
  document.removeEventListener('mouseup', stopResize);
};

const handleDragStart = (event) => {
  // Don't start drag if clicking on resize handles
  if (resizing.value) return;

  event.preventDefault();
  event.stopPropagation();

  dragging.value = true;
  dragStartX.value = event.clientX;
  dragStartTime.value = props.startTime;

  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
};

const onDrag = (event) => {
  if (!dragging.value) return;

  const deltaX = event.clientX - dragStartX.value;
  const deltaTime = deltaX / props.pixelsPerSecond;
  const newStartTime = Math.max(0, dragStartTime.value + deltaTime);

  emit('update-start-time', {
    objectId: props.objectId,
    startTime: newStartTime,
    duration: props.duration,
    isDragging: true,
  });
};

const stopDrag = (event) => {
  if (!dragging.value) return;

  const deltaX = event.clientX - dragStartX.value;
  const deltaTime = deltaX / props.pixelsPerSecond;
  const newStartTime = Math.max(0, dragStartTime.value + deltaTime);

  emit('update-start-time', {
    objectId: props.objectId,
    startTime: newStartTime,
    duration: props.duration,
    isDragging: false,
  });

  dragging.value = false;

  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
};

onUnmounted(() => {
  document.removeEventListener('mousemove', onResize);
  document.removeEventListener('mouseup', stopResize);
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
});
</script>
